import { apiGet, apiPost, apiPatch, apiDelete } from './client';
import { apiClient } from './client';
import type {
  PendingCAListResponse,
  PendingCADetail,
  ActionResponse,
  BulkApproveRequest,
  BulkApproveResponse,
  PendingCAFilters,
} from '../pages/PendingCAEvents/pendingCa.types';

// 권위: dashboard/api/src/.../pendingca/PendingCAController.java
//   GET    /api/v1/pending-ca-events                    목록 + 필터 + 페이징 + 요약 배너
//   GET    /api/v1/pending-ca-events/{auditId}          상세
//   POST   /api/v1/pending-ca-events/{auditId}/approve  승인
//   POST   /api/v1/pending-ca-events/{auditId}/reject   반려
//   PUT    /api/v1/pending-ca-events/{auditId}          수정 후 승인
//   POST   /api/v1/pending-ca-events/{auditId}/annotations 사후 주석
//   POST   /api/v1/pending-ca-events/bulk-approve       일괄 승인
//
// envelope 언랩은 apiClient interceptor 가 자동 처리. 본 모듈은 data 만 다룬다.

const BASE = '/api/v1/pending-ca-events';

// 디스플레이가 사용하는 ApiPatch/ApiDelete 는 현재 미사용이지만, tsc strict 의 unused warn 회피를 위해
// 향후 확장 여지로 import 만 유지 (실 호출 없음).
void apiPatch;
void apiDelete;

function buildListParams(f: PendingCAFilters): Record<string, string> {
  const params: Record<string, string> = {
    page: String(Math.max(0, f.page)),
    size: String(Math.max(1, Math.min(200, f.size))),
  };
  if (f.reviewStatus.length > 0) {
    params.review_status = f.reviewStatus.join(',');
  }
  if (f.symbol.trim()) params.symbol = f.symbol.trim();
  if (f.eventType) params.event_type = f.eventType;
  if (f.confidenceMin > 0) params.confidence_min = f.confidenceMin.toFixed(2);
  if (f.from) params.from = f.from;
  if (f.to) params.to = f.to;
  return params;
}

export async function getPendingCAEvents(
  filters: PendingCAFilters
): Promise<PendingCAListResponse> {
  const params = buildListParams(filters);
  // 환경에 따라 URLSearchParams 직렬화가 깔끔하므로 직접 구성.
  const qs = new URLSearchParams(params).toString();
  return apiGet<PendingCAListResponse>(`${BASE}?${qs}`);
}

export async function getPendingCAEvent(auditId: number): Promise<PendingCADetail> {
  return apiGet<PendingCADetail>(`${BASE}/${auditId}`);
}

export async function approvePendingCA(
  auditId: number,
  note?: string
): Promise<ActionResponse> {
  return apiPost<ActionResponse>(`${BASE}/${auditId}/approve`, note ? { note } : undefined);
}

export async function rejectPendingCA(
  auditId: number,
  note: string
): Promise<ActionResponse> {
  return apiPost<ActionResponse>(`${BASE}/${auditId}/reject`, { note });
}

export async function editAndApprovePendingCA(
  auditId: number,
  extractedEvent: Record<string, unknown>,
  note?: string
): Promise<ActionResponse> {
  // PUT — apiClient 헬퍼에 put 가 없으므로 직접 호출.
  const resp = await apiClient.put<ActionResponse>(`${BASE}/${auditId}`, {
    extractedEvent,
    note,
  });
  return resp.data;
}

export async function addAnnotation(
  auditId: number,
  note: string
): Promise<ActionResponse> {
  return apiPost<ActionResponse>(`${BASE}/${auditId}/annotations`, { note });
}

export async function bulkApprovePendingCA(
  req: BulkApproveRequest
): Promise<BulkApproveResponse> {
  return apiPost<BulkApproveResponse>(`${BASE}/bulk-approve`, req);
}
